import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { api } from "../lib/apiClient";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import type { DashboardData } from "@shared/types";

const CONFIDENCE_LABEL: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  insufficient_evidence: "Insufficient evidence",
};

export function DashboardPage() {
  const { selectedProjectId, projects, loading: projectsLoading } = useProjects();
  const { role } = useCurrentAppUser();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const project = projects.find((p) => p.id === selectedProjectId);

  useEffect(() => {
    if (!selectedProjectId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<DashboardData>(`/api/projects/${selectedProjectId}/dashboard`)
      .then((result) => !cancelled && setData(result))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "Failed to load dashboard."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  if (projectsLoading) return <p className="muted">Loading…</p>;
  if (projects.length === 0) {
    return (
      <div className="cei-empty-state">
        <img src="/ceinexus-mark.svg" alt="" />
        <div>
          <strong>No project selected yet</strong>
          <span>Create a project and add contract documents to start building project intelligence.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content-col-wide command-center">
      <section className="command-hero">
        <div className="command-identity">
          <img src="/ceinexus-mark.svg" alt="" />
          <div>
            <span className="workspace-eyebrow">ACTIVE PROJECT</span>
            <h1>{project?.name ?? "Project command center"}</h1>
            <p>{project?.contractNumber ? `Contract #${project.contractNumber} · ` : ""}Field intelligence, document readiness, and team activity in one place.</p>
          </div>
        </div>
        <div className="command-actions">
          <button className="btn command-secondary" type="button" onClick={() => navigate("/contract-docs")}>Project documents</button>
          <button className="btn btn-primary" type="button" onClick={() => navigate("/ask")}>Ask CEI Nexus</button>
        </div>
      </section>

      <div className="command-shortcuts" aria-label="Engineering shortcuts">
        <button onClick={() => navigate("/ask")}><span className="shortcut-icon shortcut-icon-purple">S</span><span><strong>Structure lookup</strong><small>Drainage, elevations, dimensions</small></span></button>
        <button onClick={() => navigate("/ask")}><span className="shortcut-icon shortcut-icon-good">STA</span><span><strong>Station intelligence</strong><small>Plan/profile and nearby work</small></span></button>
        <button onClick={() => navigate("/ask")}><span className="shortcut-icon shortcut-icon-yellow">$</span><span><strong>Pay item lookup</strong><small>Quantities and contract items</small></span></button>
        <button onClick={() => navigate("/contract-docs")}><span className="shortcut-icon">DOC</span><span><strong>Source control</strong><small>Plans, provisions, directives</small></span></button>
      </div>

      <button className="dash-cta" type="button" onClick={() => navigate("/ask")}>
        <div className="dash-cta-icon">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v6A1.5 1.5 0 0 1 12.5 11H8l-3 3v-3H3.5A1.5 1.5 0 0 1 2 9.5v-6Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>
        </div>
        <div className="dash-cta-text">
          <div className="dash-cta-title">Ask about this project</div>
          <div className="dash-cta-sub">Get a source-grounded answer with citations you can open and verify against the contract documents.</div>
        </div>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="dash-cta-arrow"><path d="M6 3.5 11 8l-5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>

      {error && <div className="workspace-alert">{error}</div>}

      {loading || !data ? (
        <div className="cei-empty-state"><img src="/ceinexus-mark.svg" alt="" /><div><strong>Building project view</strong><span>Loading source readiness, answer quality, and recent activity…</span></div></div>
      ) : (
        <>
          <div className="dash-stats">
            <div className="dash-stat"><div className="dash-stat-label">Source readiness</div><div className="dash-stat-value">{data.stats.approvedSources}<span className="dash-stat-of">/{data.stats.totalSources}</span></div><div className="dash-stat-note">{data.stats.draftSources > 0 ? `${data.stats.draftSources} awaiting review` : "All sources reviewed"}</div></div>
            <div className="dash-stat"><div className="dash-stat-label">Questions this week</div><div className="dash-stat-value">{data.stats.questionsThisWeek}</div><div className="dash-stat-note">Project intelligence activity</div></div>
            <div className="dash-stat"><div className="dash-stat-label">High confidence</div><div className="dash-stat-value">{data.stats.highConfidenceRatePct === null ? "—" : <>{data.stats.highConfidenceRatePct}<span className="dash-stat-of">%</span></>}</div><div className="dash-stat-note">Last 30 days</div></div>
            <div className="dash-stat"><div className="dash-stat-label">Corrections</div><div className="dash-stat-value">{data.stats.openCorrections}</div><div className="dash-stat-note">{data.stats.openCorrections > 0 ? "Needs knowledge review" : "Knowledge loop clear"}</div></div>
            <button className="dash-stat dash-stat-link" type="button" onClick={() => navigate("/escalations")}><div className="dash-stat-label">Expert questions</div><div className="dash-stat-value">{data.stats.openEscalations}</div><div className="dash-stat-note">{data.stats.openEscalations > 0 ? "Waiting on a colleague" : "Nothing open"}</div></button>
          </div>

          <div className="dash-columns">
            <div className="dash-panel">
              <div className={data.needsAttention.length === 0 ? "dash-panel-head dash-panel-head-clear" : "dash-panel-head dash-panel-head-alert"}>
                {data.needsAttention.length === 0 ? "All clear" : "Needs attention"}
              </div>
              {data.needsAttention.length === 0 ? <div className="dash-panel-empty">Project knowledge is current. Nothing is waiting on approval.</div> : data.needsAttention.map((item) => (
                <div className="dash-attn-row" key={`${item.documentId}-${item.reason}`}>
                  <div className="dash-attn-text">
                    <div className="dash-attn-title">{item.title}</div>
                    <div className="dash-attn-sub">
                      {item.reason === "table_review"
                        ? `${item.pendingTableCount} table${item.pendingTableCount === 1 ? "" : "s"} need${item.pendingTableCount === 1 ? "s" : ""} review`
                        : `${item.sourceType.replace(/_/g, " ")} · uploaded ${formatRelativeTime(item.uploadedAt)}${role === "owner" ? " · awaiting owner approval" : ""}`}
                    </div>
                  </div>
                  {role === "owner" && <button className="btn" type="button" onClick={() => navigate("/sources")}>Review</button>}
                </div>
              ))}
            </div>

            <div className="dash-panel">
              <div className="dash-panel-head">Recent intelligence</div>
              {data.recentActivity.length === 0 ? <div className="dash-panel-empty">No project activity yet.</div> : data.recentActivity.map((item, i) => (
                <div className="dash-activity-row" key={i}>
                  {item.kind === "question" && item.confidence ? <span className={`confidence-pill confidence-${item.confidence}`} style={{ flexShrink: 0 }}>{CONFIDENCE_LABEL[item.confidence]}</span> : <span className="status-chip status-approved" style={{ flexShrink: 0 }}>Approved</span>}
                  <div className="dash-activity-text"><div className="dash-activity-title">{item.kind === "question" ? `“${item.text}”` : `${item.text} approved`}</div><div className="dash-activity-sub">{item.by} · {formatRelativeTime(item.at)}</div></div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
